import { BC_REPORTS, PARTNERS } from "app_constants";
import { ICON_TYPE } from "components/icons/icon";
import {
  DateRangeSupport,
  QueryParamDateFormat,
  formatForQueryParams,
} from "helpers/shared/formatters/dates";

import { LD_FEATURE_FLAGS } from "types/context";
import type { ISectionProps } from "types/navigation";

const REPORTS: ISectionProps = {
  feature: "Reports",
  icon: ICON_TYPE.REPORTS,
  level: ["store", "brand"],
  path: "/reports",
  dateSupport: DateRangeSupport.NONE,
  subSections: [
    {
      feature: "Sales Insights",
      app: PARTNERS,
      level: ["store", "brand"],
      scope: ["staff", "superuser"],
      path: "/reports/sales_insights",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/operations/sales/" +
          formatForQueryParams(startDate, QueryParamDateFormat.UPSERVE) +
          "..." +
          formatForQueryParams(endDate, QueryParamDateFormat.UPSERVE) +
          "/net"
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Check Details",
      app: BC_REPORTS,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/reports/checks",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/reports/checks" +
          "?startDate=" +
          formatForQueryParams(startDate, QueryParamDateFormat.BREADCRUMB) +
          "&endDate=" +
          formatForQueryParams(endDate, QueryParamDateFormat.BREADCRUMB)
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Guest Book",
      app: PARTNERS,
      level: ["store", "brand"],
      scope: ["staff", "superuser"],
      path: "/reports/guestbook",
      legacyPath: () => `/marketing/guestbook`,
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "Loyalty Report",
      app: PARTNERS,
      level: ["brand"],
      scope: ["staff", "superuser"],
      path: "/reports/loyalty",
      legacyPath: () => "/marketing/loyalty_program/summary",
      dateSupport: DateRangeSupport.NONE,
    },
    {
      feature: "PMix",
      app: BC_REPORTS,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/reports/pmix",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/reports/pmix/categories" +
          "?startDate=" +
          formatForQueryParams(startDate, QueryParamDateFormat.BREADCRUMB) +
          "&endDate=" +
          formatForQueryParams(endDate, QueryParamDateFormat.BREADCRUMB)
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Menu Insights",
      app: PARTNERS,
      level: ["store", "brand"],
      scope: ["staff", "superuser"],
      path: "/reports/menu_insights",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/operations/menu/" +
          formatForQueryParams(startDate, QueryParamDateFormat.UPSERVE) +
          "..." +
          formatForQueryParams(endDate, QueryParamDateFormat.UPSERVE) +
          "/most_popular"
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Labor Report",
      app: BC_REPORTS,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/reports/labor",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/reports/labor/timecards" +
          "?startDate=" +
          formatForQueryParams(startDate, QueryParamDateFormat.BREADCRUMB) +
          "&endDate=" +
          formatForQueryParams(endDate, QueryParamDateFormat.BREADCRUMB)
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Timesheets",
      app: BC_REPORTS,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/reports/timesheets",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/reports/labor/master_checkouts" +
          "?startDate=" +
          formatForQueryParams(startDate, QueryParamDateFormat.BREADCRUMB) +
          "&endDate=" +
          formatForQueryParams(endDate, QueryParamDateFormat.BREADCRUMB)
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Labor Cost Insights",
      app: PARTNERS,
      level: ["store", "brand"],
      scope: ["staff", "superuser"],
      path: "/reports/labor_cost_insights",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/operations/labor/" +
          formatForQueryParams(startDate, QueryParamDateFormat.UPSERVE) +
          "..." +
          formatForQueryParams(endDate, QueryParamDateFormat.UPSERVE)
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Comps",
      app: BC_REPORTS,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/reports/comps",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/reports/comps" +
          "?startDate=" +
          formatForQueryParams(startDate, QueryParamDateFormat.BREADCRUMB) +
          "&endDate=" +
          formatForQueryParams(endDate, QueryParamDateFormat.BREADCRUMB)
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Voids",
      app: BC_REPORTS,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/reports/voids",
      legacyPath: ({ startDate, endDate }) => {
        return (
          "/reports/voids" +
          "?startDate=" +
          formatForQueryParams(startDate, QueryParamDateFormat.BREADCRUMB) +
          "&endDate=" +
          formatForQueryParams(endDate, QueryParamDateFormat.BREADCRUMB)
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
    {
      feature: "Server Insights",
      app: PARTNERS,
      level: ["store", "brand"],
      scope: ["staff", "superuser"],
      path: "/reports/server_insights",
      legacyPath: ({ startDate, endDate, featureFlags }) => {
        let landingPage = "/all-shifts/checks";

        if (!featureFlags[LD_FEATURE_FLAGS.showServersSalesByCheckFirst]) {
          landingPage = "/all-shifts/covers";
        }

        return (
          "/operations/servers/" +
          formatForQueryParams(startDate, QueryParamDateFormat.UPSERVE) +
          "..." +
          formatForQueryParams(endDate, QueryParamDateFormat.UPSERVE) +
          landingPage
        );
      },
      dateSupport: DateRangeSupport.FULL,
    },
  ],
};

export default REPORTS;
